import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@hero/hero-auth/v2/AuthProvider';
import { useSetNotification } from '../../../context/Notification';
import { useUserStatusContext } from '../../../context/UserStatus';
import envVars from '../../../constants/envVars';
import { ORGANIZATION_MEMBER_LIST_KEY } from './constants';

interface BulkAssignCsvResponse {
    processed: {
        care_manager_email: string;
        care_manager_id: number;
        member_external_ids: string[];
    }[];
    errors: string[];
}

const useBulkAssignCareManagersCsv = (onSuccess?: () => void) => {
    const { auth } = useAuthContext();
    const { userStatus, activeOrgId } = useUserStatusContext();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();

    const request = async (file: File): Promise<BulkAssignCsvResponse> => {
        if (!auth?.access_token || !userStatus) {
            throw new Error('Auth not provided');
        }

        const formData = new FormData();
        formData.append('file', file);

        const url = `${envVars.API_RTM_HOSTNAME}enterprise/care-manager/assign-csv/?hero_organization_id=${activeOrgId ?? userStatus.hero_organization_id}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${auth.access_token}`
            },
            body: formData
        });

        const data = await response.json();

        if (!response.ok) {
            throw data;
        }

        return data;
    };

    return useMutation({
        mutationFn: request,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [ORGANIZATION_MEMBER_LIST_KEY] });
            const processedCount = data.processed.length;
            const errorCount = data.errors.length;

            if (errorCount > 0) {
                setNotification({
                    message: `Assigned ${processedCount} care manager(s) with ${errorCount} error(s)`
                });
            } else {
                setNotification({
                    message: `Successfully assigned ${processedCount} care manager(s) via CSV`
                });
            }
            onSuccess && onSuccess();
        },
        onError: (error?: { errors: string[] }) => {
            return error?.errors?.[0] || 'An error occurred while processing the CSV';
        }
    });
};

export default useBulkAssignCareManagersCsv;
